import { type ReactNode, Suspense } from 'react'
import { Icon } from '@/components/Icon'
import { links } from '@/utils/link'
import { observer } from 'mobx-react-lite'
import { NotificationsSidebarHostContainer } from '@/features/notifications/components/NotificationsSidebar/NotificationsSidebarHostContainer'
import { useRootStore } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'
import {
  Navigation,
  type NavigationPage,
} from '@/features/shell/global-navigation/Navigation'
import { ProfileDropdownContainer } from '@/features/shell/global-navigation/Navigation/ProfileDropdown/ProfileDropdownContainer'

export interface NavigationContainerProps {
  floating: boolean
  children?: ReactNode
}

export const NavigationContainer = observer(function NavigationContainer({
  children,
  floating,
}: NavigationContainerProps) {
  const { shell } = useRootStore()
  const vm = shell.internal.globalNavigationVm
  useActivation(vm)

  const pages: NavigationPage[] = [
    {
      name: 'dashboard',
      path: links.firmDashboard(),
      hidden: !vm.canViewFirmDashboard,
      exact: true,
      icon: <Icon name="dashboard" />,
      title: 'Dashboard',
    },
    {
      name: 'opportunities',
      path: links.opportunities(),
      hidden: !vm.canViewOpportunities,
      icon: <Icon name="opportunities" />,
      title: 'Opportunities',
      badgeCount: vm.opportunityCount,
    },
    {
      name: 'jobs',
      path: links.jobs(),
      hidden: !vm.canViewJobs,
      icon: <Icon name="jobs" />,
      title: 'My Jobs',
      badgeCount: vm.unreadMessagesCount,
    },
    {
      name: 'users',
      path: links.userManagement(),
      hidden: !vm.canAccessUserManagement,
      icon: <Icon name="users" />,
      title: 'User Management',
    },
    {
      name: 'clients',
      path: links.clientUserManagement(),
      hidden: !vm.canAccessClientUserManagement,
      icon: <Icon name="users" />,
      title: 'Client Management',
    },
    {
      name: 'teams',
      path: `${links.teams()}`,
      hidden: !vm.canViewTeams,
      icon: <Icon name="teams" />,
      title: 'Teams',
    },
    {
      name: 'documents-root',
      path: links.documents(),
      hidden: !vm.canViewDocuments,
      icon: <Icon name="documents" />,
      title: 'Documents Center',
    },
    {
      name: 'pro-payouts',
      path: links.proPayouts(),
      hidden: !vm.canViewProPayouts,
      icon: <Icon name="payout" />,
      title: 'Payouts',
    },
    {
      name: 'scoping-jobs',
      path: links.scopingJobs(),
      hidden: !vm.canViewScopingJobs,
      icon: <Icon name="ocr" />,
      title: 'Scoping jobs',
    },
  ]

  return (
    <Navigation
      floating={floating}
      logoIconWhite={shell.shellInfo.invertedIconUrl}
      pages={pages}
      canDraftJob={vm.canDraftJob}
      onNewJob={vm.createJob}
      canSwitchWorkspaces={vm.canSwitchWorkspaces}
      isWorkspaceSelectorOpen={vm.isWorkspaceSelectorOpen}
      onToggleWorkspaceSelector={vm.toggleWorkspaceSelector}
      notificationCount={vm.notificationCount}
      onToggleNotifications={vm.toggleNotifications}
      accountAvatar={{
        avatar: vm.member.avatar,
        avatarFallback: vm.member.avatarFallback,
        displayName: vm.member.displayName,
      }}
      onAccountPress={vm.profileMenu.show}
      profileDropdown={<ProfileDropdownContainer vm={vm} />}
      notificationsSidebar={
        <Suspense fallback={null}>
          <NotificationsSidebarHostContainer />
        </Suspense>
      }
    >
      {children}
    </Navigation>
  )
})
